import { Markdown } from '@cadstrom/sigma-engine-shared';
import { $remark } from '@milkdown/kit/utils';
import { visit } from 'unist-util-visit';

import {
	AllDirectiveTypes,
	collectText,
	getContainerDirectiveTitleNodes,
	isDirectiveNode,
	isParentNode,
} from './markdown-plugins-utils.js';
import { RemarkMarkdownPlugins } from './markdown-plugins.js';

import type { Paragraph, Text } from 'mdast';
import type { Directives } from 'mdast-util-directive';
import type { DirectivePlugin } from './markdown-plugins-utils.js';

/**
 * Builds a remark plugin that turns any directive whose name is not in `recognizedNames` back into
 * plain text. A bare colon followed by a non-space character parses as a directive (e.g. `2:1` yields
 * a directive named `1`); without a handler such a directive is dropped on render and makes the
 * Milkdown editor throw, so incidental colon syntax must be restored to literal text.
 *
 * Run this after every named-directive plugin so only genuinely unrecognized directives are replaced.
 * The literal source is recovered from the node's position offsets when available (exact), otherwise
 * reconstructed from the directive's name, label, and attributes.
 *
 * @param recognizedNames - Directive names that have a handler and must be left untouched.
 */
export function UnknownDirectiveFallbackPlugin(recognizedNames: readonly string[]): DirectivePlugin {
	const recognized = new Set(recognizedNames);

	return () => (tree, file) => {
		const source = typeof file.value === 'string' ? file.value : undefined;

		visit(tree, [...AllDirectiveTypes], (node, index, parent) => {
			if (!isDirectiveNode(node) || index === undefined || !isParentNode(parent) || recognized.has(node.name)) {
				return;
			}

			parent.children.splice(index, 1, buildUnknownDirectiveReplacement(node, source));
			return index + 1;
		});
	};
}

/**
 * Unified plugin instance preconfigured with every directive name registered in
 * {@link RemarkMarkdownPlugins}; suitable for the react-markdown viewer's `remarkPlugins` array.
 */
export const unknownDirectiveFallbackPlugin = UnknownDirectiveFallbackPlugin(Object.keys(RemarkMarkdownPlugins));

/**
 * Milkdown `$remark` wrapper around {@link unknownDirectiveFallbackPlugin}, registered after the
 * named-directive `$remark` plugins so the editor never sees a directive its node schema cannot
 * parse.
 */
export const unknownDirectiveFallbackRemarkEditorPlugin = $remark(
	'markdown-editor-unknown-directive-fallback',
	() => unknownDirectiveFallbackPlugin,
	{},
);

function buildUnknownDirectiveReplacement(node: Directives, source: string | undefined): Text | Paragraph {
	const literal = getDirectiveLiteralSource(node, source);

	if (node.type === Markdown.NodeTypes.TextDirective) {
		return { type: 'text', value: literal };
	}

	return { type: 'paragraph', children: [{ type: 'text', value: literal }] };
}

function getDirectiveLiteralSource(node: Directives, source: string | undefined): string {
	const startOffset = node.position?.start.offset;
	const endOffset = node.position?.end.offset;

	if (source !== undefined && startOffset !== undefined && endOffset !== undefined && endOffset > startOffset) {
		return source.slice(startOffset, endOffset);
	}

	return reconstructDirectiveSource(node);
}

function reconstructDirectiveSource(node: Directives): string {
	return `${getDirectiveMarker(node.type)}${node.name}${reconstructDirectiveLabel(node)}${reconstructDirectiveAttributes(node.attributes)}`;
}

function getDirectiveMarker(type: Directives['type']): string {
	switch (type) {
		case Markdown.NodeTypes.LeafDirective: {
			return '::';
		}
		case Markdown.NodeTypes.ContainerDirective: {
			return ':::';
		}
		default: {
			return ':';
		}
	}
}

function reconstructDirectiveLabel(node: Directives): string {
	const labelNodes =
		node.type === Markdown.NodeTypes.ContainerDirective ? getContainerDirectiveTitleNodes(node) : node.children;
	const label = collectText(labelNodes);

	return label === '' ? '' : `[${label}]`;
}

function reconstructDirectiveAttributes(attributes: Directives['attributes']): string {
	if (attributes === null || attributes === undefined) {
		return '';
	}

	const parts: string[] = [];
	for (const [key, rawValue] of Object.entries(attributes)) {
		const value = rawValue ?? '';
		if (key === 'id') {
			parts.push(`#${value}`);
		} else if (key === 'class') {
			for (const className of value.split(/\s+/).filter((entry) => entry !== '')) {
				parts.push(`.${className}`);
			}
		} else {
			parts.push(value === '' ? key : `${key}="${value}"`);
		}
	}

	return parts.length === 0 ? '' : `{${parts.join(' ')}}`;
}
